import React, { useEffect } from "react";
import { FormControl, InputLabel, TextField, Box, makeStyles } from "@material-ui/core";
import { HelpIcon, HelpProps } from "./HelpIcon";
import clsx from "clsx";

export type TextInputProps = {
  label?: {
    id?: string;
    title?: string;
  };
  help?: HelpProps;
  input?: {
    value?: string;
    onChange?: Function;
    placeholderText?: string;
    helperText?: string;
    hasError?: boolean;
    id?: string;
    inputClass?: string;
    name?: string;
    autoComplete?: string;
  };
  forwardedRef?: React.RefObject<HTMLDivElement>;
};

const useStyles = makeStyles(() => ({
  box: {
    display: "flex",
    alignItems: "center",
  },
}));

export const TextInputGroupBase: React.FC<TextInputProps> = (props: TextInputProps) => {
  const css = useStyles();

  const labelElement = props.label?.title && (
    <InputLabel id={props.label?.id} shrink={false}>
      {props.label?.title}
    </InputLabel>
  );

  const help = props.help;
  const helpElement = help && (help.title || help.description) ? <HelpIcon title={help.title} description={help.description} /> : null;

  let labelArea;
  if (labelElement) {
    labelArea = (
      <Box className={css.box}>
        {labelElement}
        {helpElement}
      </Box>
    );
  }

  const hasError = props.input?.hasError;

  useEffect(() => {
    const timer = setTimeout(() => {
      const input = document.querySelector(".Mui-error>input") as HTMLInputElement;
      input?.focus();
    }, 1);
    return () => clearTimeout(timer);
  }, [hasError]);

  function handleOnBlur(currentValue?: string, newValue?: string, changeEvent?: Function) {
    if (changeEvent && currentValue !== newValue) {
      changeEvent(newValue);
    }
  }

  function handleOnChange(newValue?: string) {
    setInputValue(newValue);
  }

  const [inputValue, setInputValue] = React.useState<string | undefined>(props.input?.value);

  return (
    <Box marginY={1} id={"ws-input-" + props.input?.id}>
      <FormControl fullWidth>
        {labelArea}
        <TextField
          className={clsx(props?.input?.inputClass)}
          id={props.input?.id}
          name={props.input?.name}
          autoComplete={props.input?.autoComplete}
          placeholder={props.input?.placeholderText}
          variant='outlined'
          helperText={props.input?.helperText}
          error={props.input?.hasError}
          inputRef={props.forwardedRef}
          // combination of both allows us to switch pages while still saving input values
          onChange={event => handleOnChange(event.target.value)} // update local state on value change
          onBlur={e => handleOnBlur(props.input?.value, e.target.value, props.input?.onChange)} // update state in store on focus lost
          value={inputValue}
        />
      </FormControl>
    </Box>
  );
};
